import type { FC, ReactElement } from "react";
import {
	DataCard,
	type Props as DataCardProps,
} from "web_myplann/Shared/Components/DataCard";
import { DataNote } from "web_myplann/Shared/Components/DataNote";
import { DataRow } from "web_myplann/Shared/Components/DataRow";

type RowItem = {
	type?: "row";
	label: string;
	value: string | number | null | undefined;
	valueTooltip?: string;
	link?: string;
	element?: ReactElement;
	labelTooltip?: string;
	onLabelTooltipClick?: () => void;
};

type NoteItem = {
	type: "note";
	label: string | null | undefined;
	text: string | null | undefined;
	maxLines?: number;
};

export type DataRowCardItem = RowItem | NoteItem;

type Props = DataCardProps & {
	items: DataRowCardItem[];
	/** Computed from `items` — a card is empty when no item renders anything. */
	isEmpty?: never;
	children?: never;
};

// Mirrors the hide conditions of DataRow (value/element/tooltips) and
// DataNote (text), so the card hides exactly when all its items do.
const isItemVisible = (item: DataRowCardItem) =>
	item.type === "note"
		? !!item.text
		: (item.value != null && item.value !== "") ||
			!!item.element ||
			!!item.labelTooltip ||
			!!item.onLabelTooltipClick;

export const DataRowCard: FC<Props> = ({ items, ...dataCardProps }) => (
	<DataCard {...dataCardProps} isEmpty={!items.some(isItemVisible)}>
		{items.map((item) =>
			item.type === "note" ? (
				<DataNote
					key={item.label ?? "note"}
					label={item.label}
					text={item.text}
					maxLines={item.maxLines}
				/>
			) : (
				<DataRow
					key={item.label}
					label={item.label}
					value={item.value}
					valueTooltip={item.valueTooltip}
					link={item.link}
					element={item.element}
					labelTooltip={item.labelTooltip}
					onLabelTooltipClick={item.onLabelTooltipClick}
				/>
			),
		)}
	</DataCard>
);
